import { formatDate } from '@angular/common';

interface Coordinate {
  lat: number;
  lng: number;
}

/**
 * Extract coordinates from KML XML document.
 * Made public so boundary editor can reuse this for KML file import.
 */
export function extractCoordinatesFromKml(xmlDoc: Document): Coordinate[] {
    const coordinates: Coordinate[] = [];

    // Try to find LineString or Polygon coordinates
    const coordinateElements = xmlDoc.getElementsByTagName('coordinates');

    for (let i = 0; i < coordinateElements.length; i++) {
      const coordText = coordinateElements[i].textContent?.trim();
      if (!coordText) continue;

      // KML coordinates are in format: longitude,latitude,altitude (space or newline separated)
      const coordPairs = coordText.split(/\s+/).filter(s => s.length > 0);

      for (const pair of coordPairs) {
        const parts = pair.split(',');
        if (parts.length >= 2) {
          const lng = parseFloat(parts[0]);
          const lat = parseFloat(parts[1]);

          if (!isNaN(lat) && !isNaN(lng)) {
            coordinates.push({ lat, lng });
          }
        }
      }
    }

    // Also try to extract from Placemark/Point elements
    if (coordinates.length === 0) {
      const pointElements = xmlDoc.getElementsByTagName('Point');
      for (let i = 0; i < pointElements.length; i++) {
        const coordElement = pointElements[i].getElementsByTagName('coordinates')[0];
        if (coordElement) {
          const coordText = coordElement.textContent?.trim();
          if (coordText) {
            const parts = coordText.split(',');
            if (parts.length >= 2) {
              const lng = parseFloat(parts[0]);
              const lat = parseFloat(parts[1]);
              if (!isNaN(lat) && !isNaN(lng)) {
                coordinates.push({ lat, lng });
              }
            }
          }
        }
      }
    }

    return coordinates;
}

/**
 * Read a value from KML <ExtendedData><Data name="..."><value>...</value></Data>.
 * @Returns null if the field isn't present.
 */
export function getExtendedDataValueFromKml(xmlDoc: Document, name: string): string | null {
    const dataElements = xmlDoc.getElementsByTagName('Data');
    for (let i = 0; i < dataElements.length; i++) {
      if (dataElements[i].getAttribute('name')?.trim() === name) {
        return dataElements[i].getElementsByTagName('value')[0]?.textContent?.trim() ?? null;
      }
    }
  return null;
}

/**
 * Read a numeric value from KML <ExtendedData><Data name="..."><value>...</value></Data>.
 * @Returns null if the field isn't present or isn't a valid number.
 */
export function getExtendedDataNumberFromKml(xmlDoc: Document, name: string): number | null {
    const value = parseFloat(getExtendedDataValueFromKml(xmlDoc, name) ?? '');
    return isNaN(value) ? null : value;
}

/**
 * Read the flight start/end from KML:
 *  - <TimeSpan> with <begin> and <end>  -> use both
 *  - <TimeSpan> with only <begin>       -> begin, end = begin + flightTime
 *  - <TimeStamp> with <when>            -> when, end = when + flightTime
 * flightTime is the ExtendedData "Flight Time" duration in milliseconds.
 * 
 * @Returns An object with startTime and endTime as formatted date strings, or null if not available.
 */
export function getTimeSpanFromKml(xmlDoc: Document, flightTime: number | null): { startTime: string | null; endTime: string | null } {
    const begin = xmlDoc.querySelector('TimeSpan > begin')?.textContent?.trim();
    const end = xmlDoc.querySelector('TimeSpan > end')?.textContent?.trim();
    const when = xmlDoc.querySelector('TimeStamp > when')?.textContent?.trim();
   
    // Case when both begin and end are present in the TimeSpan
    if(begin && end){
        return { startTime: formatKMLDate(begin), endTime: formatKMLDate(end) };
    }
    // Case when theres only a begin or when
    const start = begin || when;
    if(start){
        const startTime = formatKMLDate(start);
        return { startTime, endTime: addDuration(startTime, flightTime) };
      }

    return { startTime: null, endTime: null };
}

/**
 * 
 * Potential KML file date formats:
 *  xsd:gYear(YYYY)
 *  xsd:gYearMonth(YYYY-MM)
 *  xsd:date(YYYY-MM-DD)
 *  xsd:dateTime(YYYY-MM-DDTHH:mm:ssZ)
 * 
 * @Returns A formatted date string: yyyy-MM-dd or yyyy-MM-ddTHH:mm:ss
 */
export function formatKMLDate(dateString: string | null): string | null {
    const value = dateString?.trim();
    if (!value) return null;
    const hasTime = value.includes('T');
    try {
      return formatDate(value, hasTime ? "yyyy-MM-dd'T'HH:mm:ss" : 'yyyy-MM-dd', 'en-US');
    } catch {
      return null;
    }
}

/**
 * Add a duration (in milliseconds) to a start time string.
 * @Returns A formatted string: yyyy-MM-ddTHH:mm:ss
 */
export function addDuration(startTime: string | null, durationMs: number | null): string | null {
    if (!startTime?.includes('T') || !durationMs) return null;
    const end = new Date(new Date(startTime).getTime() + durationMs);
    return formatDate(end, "yyyy-MM-dd'T'HH:mm:ss", 'en-US');
}

/** Round to 3 decimal places (e.g. 3.4000000953674316 -> 3.4) */
export const round3 = (value: number) => Math.round(value * 1000) / 1000;

// KML flight data is metric; the app stores US units
export const metersToFeet = (m: number) => Math.round(m * 3.28084 * 10) / 10;
export const msToMph = (ms: number) => Math.round(ms * 2.23694 * 10) / 10;

